<template>
  <div class="min-h-screen">
    <NavBar :signed-in="signedIn" :user-name="userName" active-page="messages" />
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <a href="/conversations" class="text-sm text-blue-700 hover:text-blue-900 mb-4 inline-block">&larr; {{ t('messages.back_to_inbox') }}</a>

      <!-- Header -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6 flex items-center gap-3">
        <span class="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-sm font-medium text-blue-800 shrink-0">
          {{ otherUser?.name?.charAt(0)?.toUpperCase() || '?' }}
        </span>
        <span class="text-lg font-semibold text-gray-900">{{ otherUser?.name || t('messages.unknown_user') }}</span>
      </div>

      <!-- Messages -->
      <div ref="messagesContainer" class="space-y-3 mb-6 max-h-[60vh] overflow-y-auto px-1">
        <p v-if="msgs.length === 0" class="text-center text-gray-400 text-sm py-8">{{ t('messages.no_messages') }}</p>

        <div v-for="msg in msgs" :key="msg.id"
             :class="['flex', msg.user.id === userId ? 'justify-end' : 'justify-start']">
          <div :class="['max-w-[75%] rounded-xl px-4 py-2 relative group',
                        msg.user.id === userId ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-900']">
            <p class="text-sm whitespace-pre-line">{{ msg.body }}</p>
            <div :class="['flex items-center gap-2 mt-1', msg.user.id === userId ? 'justify-end' : 'justify-start']">
              <span :class="['text-xs', msg.user.id === userId ? 'text-blue-200' : 'text-gray-400']">
                {{ formatTime(msg.created_at) }}
              </span>
              <button v-if="msg.user.id === userId"
                      @click="deleteMessage(msg)"
                      :class="['text-xs opacity-0 group-hover:opacity-100 transition', msg.user.id === userId ? 'text-blue-200 hover:text-white' : 'text-red-400 hover:text-red-600']">
                {{ t('messages.delete') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Input -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <form @submit.prevent="sendMessage">
          <div class="flex gap-2 items-center">
            <input ref="messageInput" v-model="newMessage" type="text" :placeholder="t('messages.placeholder')" required
                   class="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <div class="relative">
              <button type="button" @click="showEmojis = !showEmojis" class="text-gray-400 hover:text-gray-600 text-xl p-1">😊</button>
              <div v-if="showEmojis" class="absolute bottom-full right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-50 w-64 flex flex-wrap gap-1">
                <button v-for="e in emojis" :key="e" type="button" @click="addEmoji(e)" class="text-xl hover:bg-gray-100 rounded p-1 cursor-pointer">{{ e }}</button>
              </div>
            </div>
            <button type="submit" :disabled="!newMessage.trim()"
                    class="px-4 py-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 transition disabled:opacity-50">
              {{ t('messages.send') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import NavBar from './NavBar.vue'

const props = defineProps({
  signedIn: Boolean, userName: String, userId: Number,
  conversationId: Number, otherUser: Object, messages: Array, csrfToken: String
})
const { t } = useI18n()

const msgs = ref([...props.messages])
const newMessage = ref('')
const messageInput = ref(null)
const messagesContainer = ref(null)
const showEmojis = ref(false)
const emojis = ['😊', '😂', '🤣', '😍', '🥰', '😎', '🤔', '😢', '😡', '👍', '👎', '👏', '🙌', '💪', '🏃', '🏃‍♀️', '🏅', '🥇', '🥈', '🥉', '🎉', '🔥', '❤️', '💯', '⭐', '🌟', '☀️', '🌧️', '💨', '🗺️', '📍', '⏱️', '🎯', '✅', '❌', '⚡']

function addEmoji(e) {
  newMessage.value += e
  showEmojis.value = false
  if (messageInput.value) messageInput.value.focus()
}

function formatTime(d) {
  const date = new Date(d)
  const now = new Date()
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' ' +
    date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

async function request(url, method, body = null) {
  const o = { method, headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' } }
  if (body) o.body = JSON.stringify(body)
  return fetch(url, o)
}

async function sendMessage() {
  if (!newMessage.value.trim()) return
  try {
    const res = await request(`/conversations/${props.conversationId}/messages`, 'POST', { message: { body: newMessage.value } })
    if (res.ok) {
      msgs.value.push(await res.json())
      newMessage.value = ''
      scrollToBottom()
    }
  } catch {}
}

async function deleteMessage(msg) {
  if (!confirm(t('messages.confirm_delete'))) return
  try {
    const res = await request(`/conversations/${props.conversationId}/messages/${msg.id}`, 'DELETE')
    if (res.ok) msgs.value = msgs.value.filter(m => m.id !== msg.id)
  } catch {}
}

async function pollMessages() {
  try {
    const res = await fetch(`/conversations/${props.conversationId}.json`, {
      headers: { 'Accept': 'application/json', 'X-CSRF-Token': props.csrfToken }
    })
    if (res.ok) {
      const data = await res.json()
      const newMsgs = data.messages || []
      if (newMsgs.length > msgs.value.length) {
        msgs.value = newMsgs
        scrollToBottom()
      }
    }
  } catch {}
}

let pollInterval = null

onMounted(() => {
  scrollToBottom()
  pollInterval = setInterval(pollMessages, 3000)
})

onUnmounted(() => {
  if (pollInterval) clearInterval(pollInterval)
})
</script>
